import React, { useState } from 'react';
import { X, Bell, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface EmailCaptureModalProps {
  keyword: string;
  availableCount: number;
  onDismiss: () => void;
}

const SESSION_KEY = 'dombase_email_captured';

const EmailCaptureModal: React.FC<EmailCaptureModalProps> = ({ keyword, availableCount, onDismiss }) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.includes('@')) { setError('Please enter a valid email.'); return; }
    setLoading(true);
    setError('');
    try {
      await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, keyword, source: 'scan_complete' }),
      });
      sessionStorage.setItem(SESSION_KEY, '1');
      setSubmitted(true);
      setTimeout(onDismiss, 2500);
    } catch {
      setError('Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onDismiss} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 animate-in slide-in-from-bottom-4 sm:slide-in-from-bottom-0 duration-300">
        <button
          onClick={onDismiss}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 p-1 rounded"
        >
          <X className="h-4 w-4" />
        </button>

        {submitted ? (
          <div className="text-center py-2">
            <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto mb-3" />
            <p className="font-semibold text-gray-900">You're in!</p>
            <p className="text-sm text-gray-500 mt-1">We'll alert you if any of your domains change status.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Bell className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 text-sm">Save your results</p>
                <p className="text-xs text-gray-500">
                  {availableCount > 0
                    ? `${availableCount} domain${availableCount > 1 ? 's' : ''} available for "${keyword}"`
                    : `Get notified when domains for "${keyword}" open up`}
                </p>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Get alerts when watched domains change status or drop in price. No spam — one email per change.
            </p>

            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
              {error && <p className="text-xs text-red-500">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Saving…' : 'Notify me'}
              </Button>
            </form>

            <button
              onClick={onDismiss}
              className="mt-3 w-full text-xs text-gray-400 hover:text-gray-600 text-center"
            >
              No thanks
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export { SESSION_KEY };
export default EmailCaptureModal;
